import { effectScope, onScopeDispose } from 'vue';

/**
 * Creates a shared version of a composable that will be initialized only once
 * and reused across multiple component instances.
 *
 * This utility manages the lifecycle of a composable by:
 * - Creating the composable only when first requested
 * - Tracking the number of components using it
 * - Automatically disposing resources when no components are using it
 *
 * @see {@link https://github.com/vuejs/rfcs/blob/master/active-rfcs/0041-reactivity-effect-scope.md} Vue RFC 0041
 *
 * @template T
 * @param {(...args: any[]) => T} composable - The original composable function to be shared
 * @returns {(...args: any[]) => T} A wrapped version of the composable that shares state
 */
export function useCreateSharedComposable(composable) {
  let subscribers = 0;
  let state;
  let scope;

  const dispose = () => {
    // eslint-disable-next-line no-plusplus
    if (scope && --subscribers <= 0) {
      scope.stop();
      // eslint-disable-next-line no-multi-assign
      state = scope = null;
    }
  };

  return (...args) => {
    // eslint-disable-next-line no-plusplus
    subscribers++;
    if (!state) {
      scope = effectScope(true);
      state = scope.run(() => composable(...args));
    }
    onScopeDispose(dispose);
    return state;
  };
}
